import {OverrideFieldName, PlaybookIssue} from "common/types/issue";
import issue_template_utils from "./issues/issue_template_utils";
import {getIssueFieldValue} from "./get_issue_field_value";
import {shouldUseManualCorrections} from "./manual_corrections_utils";
import {Issueset} from "common/types/issueset";
import {
  Clausepart,
  DocumentClause,
  Party,
  PositiveReasonData,
  ReportId,
  TopicsById,
} from "common/types/document";
import {Project} from "common/types/project";

interface TemplateParams {
  issue: PlaybookIssue;
  issueset: Issueset;
  documentClauses: DocumentClause[];
  topicsById: TopicsById;
  positiveReasonData: PositiveReasonData;
  documentClauseparts: Clausepart;
  selectedReportId: ReportId;
  project: Project;
  parties?: Party[];
  hasReasons?: boolean;
  reportManualCorrections?: Record<string, string>;
  useManualCorrections?: boolean;
}

export const getFieldTemplateValue = (
  field: OverrideFieldName,
  params: TemplateParams,
): string | null => {
  const {
    issue,
    issueset,
    project,
    documentClauses,
    topicsById,
    positiveReasonData,
    documentClauseparts,
    parties,
    selectedReportId,
    useManualCorrections,
    hasReasons = false,
    reportManualCorrections = {},
  } = params;

  const allowManualCorrections =
    useManualCorrections ??
    shouldUseManualCorrections(field, project, selectedReportId);

  const overrideValue = getIssueFieldValue(
    issue,
    field,
    issueset.id,
    project.issue_override_tag_list,
    true,
  );

  function getManualValue(fieldKey: string) {
    const reasonsValue = reportManualCorrections[fieldKey];
    if (reasonsValue) {
      return reasonsValue;
    }

    if (allowManualCorrections && reportManualCorrections[field]) {
      return reportManualCorrections[field];
    }
    return null;
  }

  switch (field) {
    case "guidance":
      return (
        getManualValue(hasReasons ? "guidance" : "positive_guidance") ??
        overrideValue ??
        issue_template_utils.getGuidanceTemplateText(
          issue,
          issueset,
          documentClauses,
          topicsById,
          positiveReasonData,
          documentClauseparts,
          parties,
        )
      );

    case "fallback_guidance":
      return (
        getManualValue("fallback") ??
        overrideValue ??
        issue_template_utils.getFallbackTemplateText(
          issue,
          positiveReasonData,
          documentClauseparts,
          parties,
        )
      );

    case "standard_language":
      return (
        getManualValue("standard_language") ??
        overrideValue ??
        issue_template_utils.getStandardLanguageTemplateText(
          issue,
          positiveReasonData,
          documentClauseparts,
          parties,
        )
      );

    case "required_change":
      return (
        getManualValue("required_change") ??
        overrideValue ??
        issue_template_utils.getRequiredChangeTemplatedText(
          issue,
          issueset,
          documentClauses,
          topicsById,
          positiveReasonData,
          documentClauseparts,
          parties,
          selectedReportId,
        )
      );

    case "description":
      return (
        getManualValue("description") ?? overrideValue ?? issue.description
      );
    default:
      return null;
  }
};
